import { Component, createSignal, For, Show } from "solid-js";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { GithubIssuePicker } from "../import";
import { api } from "../../api/client";
import {
  fetchGithubIssues,
  importGithubIssues,
  type GitHubIssue,
  type ImportResult,
} from "../../api/workItems";

type Step = "select-repo" | "loading" | "select-issues" | "importing" | "done";

interface RepoLink {
  id: string;
  repo_owner: string;
  repo_name: string;
  is_primary: boolean;
}

interface RepoListResponse {
  count: number;
  repos: RepoLink[];
}

export interface ImportGithubModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onImportComplete: () => void;
}

export const ImportGithubModal: Component<ImportGithubModalProps> = (props) => {
  const [step, setStep] = createSignal<Step>("select-repo");
  const [repos, setRepos] = createSignal<RepoLink[]>([]);
  const [selectedRepoId, setSelectedRepoId] = createSignal<string>("");
  const [issues, setIssues] = createSignal<GitHubIssue[]>([]);
  const [selectedIssues, setSelectedIssues] = createSignal<Set<number>>(
    new Set<number>(),
  );
  const [stateFilter, setStateFilter] = createSignal<string>("open");
  const [importResult, setImportResult] = createSignal<ImportResult | null>(
    null,
  );
  const [error, setError] = createSignal<string | null>(null);
  const [loadingRepos, setLoadingRepos] = createSignal(false);

  const reset = () => {
    setStep("select-repo");
    setRepos([]);
    setSelectedRepoId("");
    setIssues([]);
    setSelectedIssues(new Set<number>());
    setStateFilter("open");
    setImportResult(null);
    setError(null);
    setLoadingRepos(false);
  };

  const handleClose = () => {
    reset();
    props.onClose();
  };

  const loadRepos = async () => {
    setLoadingRepos(true);
    try {
      const response = await api.get<RepoListResponse>(
        `/projects/${props.projectId}/github/repos`,
      );
      setRepos(response.repos);
      if (response.repos.length === 1) {
        setSelectedRepoId(response.repos[0].id);
      } else {
        const primary = response.repos.find((r) => r.is_primary);
        if (primary) setSelectedRepoId(primary.id);
      }
    } catch {
      setError("Failed to load linked repositories");
    } finally {
      setLoadingRepos(false);
    }
  };

  const loadIssues = async () => {
    const repoId = selectedRepoId();
    if (!repoId) return;

    setStep("loading");
    setError(null);
    try {
      const result = await fetchGithubIssues(repoId, stateFilter(), 1, 100);
      setIssues(result);
      setSelectedIssues(new Set(result.map((i) => i.number)));
      setStep("select-issues");
    } catch {
      setError("Failed to fetch issues from GitHub");
      setStep("select-repo");
    }
  };

  const handleImport = async () => {
    const repoId = selectedRepoId();
    const selected = selectedIssues();
    if (!repoId || selected.size === 0) return;

    setStep("importing");
    setError(null);
    try {
      const issuesToImport = issues()
        .filter((i) => selected.has(i.number))
        .map((i) => ({
          number: i.number,
          title: i.title,
          body: i.body,
          labels: i.labels.map((l) => l.name),
          state: i.state,
        }));

      const result = await importGithubIssues(
        props.projectId,
        repoId,
        issuesToImport,
      );
      setImportResult(result);
      setStep("done");
      props.onImportComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
      setStep("select-issues");
    }
  };

  const handleOpen = () => {
    if (props.open && repos().length === 0 && !loadingRepos()) {
      loadRepos();
    }
  };

  // Trigger load when open changes
  // SolidJS: use a derived expression tracked in the JSX
  const isOpen = () => {
    if (props.open) handleOpen();
    return props.open;
  };

  const stepTitle = () => {
    switch (step()) {
      case "select-repo":
        return "Import GitHub Issues — Select Repository";
      case "loading":
        return "Import GitHub Issues — Fetching...";
      case "select-issues":
        return "Import GitHub Issues — Select Issues";
      case "importing":
        return "Import GitHub Issues — Importing...";
      case "done":
        return "Import GitHub Issues — Complete";
    }
  };

  return (
    <Modal open={isOpen()} onClose={handleClose} title={stepTitle()} size="lg">
      {/* Select Repository step */}
      <Show when={step() === "select-repo"}>
        <div class="space-y-4">
          <Show when={error()}>
            <div class="px-3 py-2 bg-signal-red-tint border border-signal-red/25 rounded text-sm text-signal-red">
              {error()}
            </div>
          </Show>

          <Show
            when={!loadingRepos()}
            fallback={
              <div class="flex items-center justify-center py-8 gap-2">
                <svg
                  class="w-5 h-5 text-ink animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                <span class="text-sm text-ink-2">Loading repositories...</span>
              </div>
            }
          >
            <Show
              when={repos().length > 0}
              fallback={
                <p class="text-sm text-ink-2 py-4">
                  No GitHub repositories linked to this project. Link a
                  repository in Project Settings first.
                </p>
              }
            >
              <p class="text-sm text-ink-2">
                Select a repository to import issues from.
              </p>
              <div class="space-y-2">
                <For each={repos()}>
                  {(repo) => (
                    <label class="flex items-center gap-3 p-3 rounded border border-line cursor-pointer hover:bg-sunken transition-colors">
                      <input
                        type="radio"
                        name="repo"
                        value={repo.id}
                        checked={selectedRepoId() === repo.id}
                        onChange={() => setSelectedRepoId(repo.id)}
                        class="accent-ink"
                      />
                      <div class="flex items-center gap-2">
                        <svg
                          class="w-4 h-4 text-ink-3"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8ZM5 12.25a.25.25 0 0 1 .25-.25h3.5a.25.25 0 0 1 .25.25v3.25a.25.25 0 0 1-.4.2l-1.45-1.087a.249.249 0 0 0-.3 0L5.4 15.7a.25.25 0 0 1-.4-.2Z" />
                        </svg>
                        <span class="text-sm font-medium text-ink-2">
                          {repo.repo_owner}/{repo.repo_name}
                        </span>
                        <Show when={repo.is_primary}>
                          <span class="text-xs bg-surface text-ink-2 border border-line px-1.5 py-0.5 rounded">
                            primary
                          </span>
                        </Show>
                      </div>
                    </label>
                  )}
                </For>
              </div>

              <div class="flex items-center gap-3">
                <label class="text-sm font-medium text-ink-2">State:</label>
                <select
                  class="border border-line rounded-md px-3 py-1.5 text-sm text-ink-2 bg-surface"
                  value={stateFilter()}
                  onChange={(e) => setStateFilter(e.currentTarget.value)}
                >
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="all">All</option>
                </select>
              </div>

              <div class="flex justify-end pt-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!selectedRepoId()}
                  onClick={loadIssues}
                >
                  Fetch Issues
                </Button>
              </div>
            </Show>
          </Show>
        </div>
      </Show>

      {/* Loading step */}
      <Show when={step() === "loading"}>
        <div class="flex flex-col items-center justify-center py-10 gap-3">
          <svg
            class="w-8 h-8 text-ink animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p class="text-sm text-ink-2">Fetching issues from GitHub...</p>
        </div>
      </Show>

      {/* Select Issues step */}
      <Show when={step() === "select-issues"}>
        <div class="space-y-4">
          <Show when={error()}>
            <div class="px-3 py-2 bg-signal-red-tint border border-signal-red/25 rounded text-sm text-signal-red">
              {error()}
            </div>
          </Show>

          <GithubIssuePicker
            issues={issues()}
            selected={selectedIssues()}
            onSelectionChange={setSelectedIssues}
            showState
            emptyMessage="No issues found matching the filter."
            listClass="max-h-80 rounded border border-line"
            label={
              <p class="text-sm text-ink-2">
                {issues().length} issue(s) found. Select the ones to import.
              </p>
            }
          />

          <div class="flex justify-between pt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setStep("select-repo")}
            >
              Back
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={selectedIssues().size === 0}
              onClick={handleImport}
            >
              Import {selectedIssues().size} Issue(s)
            </Button>
          </div>
        </div>
      </Show>

      {/* Importing step */}
      <Show when={step() === "importing"}>
        <div class="flex flex-col items-center justify-center py-10 gap-3">
          <svg
            class="w-8 h-8 text-ink animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p class="text-sm text-ink-2">Importing issues...</p>
        </div>
      </Show>

      {/* Done step */}
      <Show when={step() === "done"}>
        <div class="space-y-4">
          <div class="flex items-center gap-2 text-signal-green">
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span class="text-sm font-medium">Import complete</span>
          </div>
          <Show when={importResult()}>
            {(result) => (
              <div class="space-y-2">
                <div class="flex gap-4 text-sm">
                  <span class="text-ink-2">
                    <span class="font-semibold text-signal-green">
                      {result().created}
                    </span>{" "}
                    created
                  </span>
                  <span class="text-ink-2">
                    <span class="font-semibold text-ink-3">
                      {result().skipped}
                    </span>{" "}
                    skipped
                  </span>
                </div>
                <Show when={result().errors.length > 0}>
                  <div class="rounded border border-signal-amber/25 bg-signal-amber-tint p-3 space-y-1">
                    <p class="text-xs font-medium text-signal-amber">
                      {result().errors.length} error(s):
                    </p>
                    <For each={result().errors}>
                      {(err) => (
                        <p class="text-xs text-signal-amber">
                          Row {err.row}: {err.message}
                        </p>
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            )}
          </Show>
          <div class="flex justify-end pt-2">
            <Button variant="primary" size="sm" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      </Show>
    </Modal>
  );
};
